import { useEffect, useMemo, useRef, useState } from "react";
import Layout from "@/components/eoffice/Layout";
import api, { formatApiErrorDetail, docPreviewUrl } from "@/lib/api";
import { DEPOSITORY } from "@/constants/testIds";
import { toast } from "sonner";
import { Upload, Trash2, FileText, FileType2, Download, Eye, CheckCircle2, AlertCircle, Loader2, Paperclip, X, Search, RefreshCw } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { usePaged, Pager } from "@/components/eoffice/Pager";

const ACCEPT = ".pdf,.docx,.doc";
const ACCEPT_MIME = ["application/pdf", "application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"];

const extIcon = (ext) => (ext === "pdf" ? FileText : FileType2);
const stripExt = (name) => { const i = name.lastIndexOf("."); return i > 0 ? name.slice(0, i) : name; };
const isAllowed = (f) => {
  const ext = (f.name.split(".").pop() || "").toLowerCase();
  return ["pdf", "doc", "docx"].includes(ext) || ACCEPT_MIME.includes(f.type);
};

export default function Depository() {
  const { user } = useAuth();
  const [docs, setDocs] = useState([]);
  const [openUpload, setOpenUpload] = useState(false);
  const [queue, setQueue] = useState([]);
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  // Multi-select state -------------------------------------------------------
  const [selected, setSelected] = useState(() => new Set());
  const toggleSelect = (id) => setSelected((s) => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const clearSelection = () => setSelected(new Set());
  const selectAll = () => setSelected(new Set(docs.map((d) => d.id)));
  const docsPaged = usePaged(docs, "docs");

  // Bulk-attach dialog state -------------------------------------------------
  const [attachOpen, setAttachOpen] = useState(false);
  const [attachFiles, setAttachFiles] = useState([]);       // files user can attach to
  const [attachSearch, setAttachSearch] = useState("");
  const [attachTargetId, setAttachTargetId] = useState("");
  const [attachBusy, setAttachBusy] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/documents"); setDocs(data); }
    catch { toast.error("Failed to load documents"); }
  };
  useEffect(() => { load(); }, []);

  // Keep selection in sync when a doc disappears (deleted, filtered, etc.)
  useEffect(() => {
    setSelected((s) => {
      const ids = new Set(docs.map((d) => d.id));
      const n = new Set(); s.forEach((id) => ids.has(id) && n.add(id));
      return n;
    });
  }, [docs]);

  // ---------- Upload queue ---------------------------------------------------
  const enqueueFiles = (fileList) => {
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;
    const accepted = incoming.filter(isAllowed);
    const rejected = incoming.length - accepted.length;
    if (rejected > 0) toast.error(`${rejected} file(s) skipped — only PDF and Word are allowed.`);
    if (!accepted.length) return;
    const items = accepted.map((f) => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      file: f, title: stripExt(f.name), status: "pending",
    }));
    setQueue((q) => [...q, ...items]);
    if (!openUpload) setOpenUpload(true);
  };
  const onDrop = (e) => { e.preventDefault(); setDragOver(false); enqueueFiles(e.dataTransfer.files); };
  const onDragOver = (e) => { e.preventDefault(); setDragOver(true); };
  const onDragLeave = (e) => { e.preventDefault(); setDragOver(false); };
  const removeQueued = (id) => setQueue((q) => q.filter((x) => x.id !== id));
  const updateTitle = (id, title) => setQueue((q) => q.map((x) => x.id === id ? { ...x, title } : x));

  const uploadAll = async () => {
    if (!queue.length) { toast.error("Drop or choose at least one file"); return; }
    setBusy(true);
    let succeeded = 0, failed = 0;
    for (const item of queue) {
      if (item.status === "done") { succeeded++; continue; }
      setQueue((q) => q.map((x) => x.id === item.id ? { ...x, status: "uploading", error: undefined } : x));
      const fd = new FormData();
      fd.append("file", item.file);
      fd.append("title", (item.title || stripExt(item.file.name)).trim() || item.file.name);
      fd.append("description", "");
      try {
        await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setQueue((q) => q.map((x) => x.id === item.id ? { ...x, status: "done" } : x));
        succeeded++;
      } catch (e) {
        const msg = formatApiErrorDetail(e.response?.data?.detail);
        setQueue((q) => q.map((x) => x.id === item.id ? { ...x, status: "error", error: msg } : x));
        failed++;
      }
    }
    setBusy(false);
    if (succeeded > 0) toast.success(`${succeeded} document${succeeded === 1 ? "" : "s"} uploaded`);
    if (failed > 0) toast.error(`${failed} upload${failed === 1 ? "" : "s"} failed — see the list.`);
    await load();
    if (failed === 0) { setOpenUpload(false); setQueue([]); }
    else setQueue((q) => q.filter((x) => x.status !== "done"));
  };
  const closeUploadDialog = () => { if (busy) return; setOpenUpload(false); setQueue([]); if (inputRef.current) inputRef.current.value = ""; };

  // ---------- Single delete --------------------------------------------------
  const del = async (id) => {
    if (!window.confirm("Delete this document?")) return;
    try { await api.delete(`/documents/${id}`); toast.success("Deleted"); await load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  // ---------- Replace missing file ------------------------------------------
  const replaceInputRef = useRef(null);
  const [replaceTargetId, setReplaceTargetId] = useState(null);
  const [replacing, setReplacing] = useState(false);
  const promptReplace = (id) => { setReplaceTargetId(id); replaceInputRef.current?.click(); };
  const onReplaceFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f || !replaceTargetId) return;
    if (!isAllowed(f)) { toast.error("Only PDF and Word files are allowed."); return; }
    setReplacing(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post(`/documents/${replaceTargetId}/replace`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("File replaced — the document is now available again.");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setReplacing(false); setReplaceTargetId(null);
    }
  };

  // ---------- Bulk actions ---------------------------------------------------
  const bulkDelete = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    // Client-side pre-check: user must be uploader or admin
    const notAllowed = docs.filter((d) => ids.includes(d.id) && d.uploader_id !== user?.id && user?.role !== "admin");
    if (notAllowed.length) {
      toast.error(`You can only delete documents you uploaded (${notAllowed.length} skipped).`);
    }
    const allowed = ids.filter((id) => {
      const d = docs.find((x) => x.id === id);
      return d && (d.uploader_id === user?.id || user?.role === "admin");
    });
    if (!allowed.length) { clearSelection(); return; }
    if (!window.confirm(`Delete ${allowed.length} document${allowed.length === 1 ? "" : "s"}? This cannot be undone.`)) return;
    setBusy(true);
    let ok = 0, err = 0;
    for (const id of allowed) {
      try { await api.delete(`/documents/${id}`); ok++; }
      catch { err++; }
    }
    setBusy(false);
    if (ok > 0) toast.success(`${ok} deleted`);
    if (err > 0) toast.error(`${err} failed`);
    clearSelection();
    await load();
  };

  const openBulkAttach = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    // Fetch files the user can attach to: not approved AND (creator or holder)
    try {
      const { data } = await api.get("/files?scope=all");
      const list = (data || []).filter((f) => !f.approved && (f.creator_id === user?.id || f.current_holder_id === user?.id));
      setAttachFiles(list);
      setAttachSearch(""); setAttachTargetId("");
      setAttachOpen(true);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const doBulkAttach = async () => {
    if (!attachTargetId) { toast.error("Pick a file to attach to"); return; }
    setAttachBusy(true);
    try {
      // Fetch current attachments then merge — server replaces the list on attach-docs.
      const { data: fileDetail } = await api.get(`/files/${attachTargetId}`);
      const current = new Set(fileDetail.attached_docs || []);
      Array.from(selected).forEach((id) => current.add(id));
      const merged = Array.from(current);
      await api.post(`/files/${attachTargetId}/attach-docs`, { document_ids: merged });
      toast.success(`Attached ${selected.size} doc${selected.size === 1 ? "" : "s"} to "${fileDetail.name}"`);
      setAttachOpen(false); clearSelection();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setAttachBusy(false); }
  };

  const filteredAttachFiles = useMemo(() => {
    const q = attachSearch.trim().toLowerCase();
    if (!q) return attachFiles;
    return attachFiles.filter((f) => f.name.toLowerCase().includes(q));
  }, [attachFiles, attachSearch]);

  const selectedCount = selected.size;
  const allDeletable = Array.from(selected).every((id) => {
    const d = docs.find((x) => x.id === id);
    return d && (d.uploader_id === user?.id || user?.role === "admin");
  });

  return (
    <Layout>
      <div className="px-4 md:px-10 py-6 md:py-10">
        <div className="flex flex-wrap gap-4 items-end justify-between mb-8 border-b border-[#DCD9D0] pb-6">
          <div>
            <div className="font-mono-eo text-[10px] uppercase tracking-[0.3em] text-[#8A8A80]">// Central Depository</div>
            <h1 className="font-heading text-4xl md:text-[44px] font-bold leading-none mt-2">Supporting Documents</h1>
            <p className="text-[14px] text-[#5C5C54] mt-3">Shared reference library — PDF and Word only. Select multiple to attach in bulk or delete.</p>
          </div>
          <button data-testid={DEPOSITORY.uploadBtn} onClick={() => setOpenUpload(true)} className="eo-btn-primary flex items-center gap-2"><Upload size={16} /> Upload</button>
        </div>

        {/* Sticky bulk-action bar */}
        {selectedCount > 0 && (
          <div data-testid="bulk-action-bar" className="sticky top-14 z-20 mb-4 flex items-center gap-3 px-4 py-3 bg-[#1D3557] text-white shadow-md">
            <div className="font-mono-eo text-[11px] uppercase tracking-widest">// {selectedCount} selected</div>
            <div className="flex-1" />
            <button onClick={selectAll} className="text-[12px] underline decoration-white/50 hover:decoration-white" data-testid="bulk-select-all">Select all ({docs.length})</button>
            <button onClick={clearSelection} className="text-[12px] underline decoration-white/50 hover:decoration-white" data-testid="bulk-clear">Clear</button>
            <button onClick={openBulkAttach} data-testid="bulk-attach-btn" className="bg-white text-[#1D3557] px-3 py-1.5 text-[12px] font-medium flex items-center gap-1.5 hover:bg-[#F5F5F0]">
              <Paperclip size={13} /> Attach to file...
            </button>
            <button
              onClick={bulkDelete}
              disabled={busy || !allDeletable}
              title={!allDeletable ? "You can only bulk-delete documents you uploaded." : "Delete selected"}
              data-testid="bulk-delete-btn"
              className="bg-[#E63946] text-white px-3 py-1.5 text-[12px] font-medium flex items-center gap-1.5 hover:bg-[#c22b38] disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {busy ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />} Delete selected
            </button>
          </div>
        )}

        {/* Page-level drop zone */}
        <div
          onDrop={onDrop} onDragOver={onDragOver} onDragLeave={onDragLeave}
          className={`relative ${dragOver ? "ring-2 ring-[#1D3557] ring-offset-4 ring-offset-[#F5F5F0]" : ""}`}
          data-testid="depository-drop-zone"
        >
          {dragOver && (
            <div className="absolute inset-0 z-10 bg-[#1D3557]/5 border-2 border-dashed border-[#1D3557] flex items-center justify-center pointer-events-none">
              <div className="text-center">
                <Upload size={36} className="mx-auto text-[#1D3557] mb-2" />
                <div className="font-heading font-semibold text-[18px] text-[#1D3557]">Drop to upload</div>
                <div className="font-mono-eo text-[11px] uppercase tracking-widest text-[#5C5C54] mt-1">PDF and Word only · multiple files supported</div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 border border-[#DCD9D0] bg-white">
            {docs.length === 0 && (
              <div className="col-span-full p-14 text-center">
                <FileText size={28} className="mx-auto text-[#B8B5AA] mb-3" />
                <div className="text-[14px] text-[#5C5C54]">No supporting documents yet. Drop PDFs or Word files here to bulk upload.</div>
                <button onClick={() => setOpenUpload(true)} className="eo-btn-ghost mt-4">Upload documents</button>
              </div>
            )}
            {docsPaged.items.map((d, idx) => {
              const Icon = extIcon(d.extension);
              const isSel = selected.has(d.id);
              const broken = d.available === false;
              const canReplace = d.uploader_id === user?.id || user?.role === "admin";
              return (
                <div
                  key={d.id}
                  data-testid={DEPOSITORY.docRow(d.id)}
                  className={`p-5 border-[#DCD9D0] ${idx % 3 !== 0 ? "border-l" : ""} border-b relative group cursor-pointer transition-colors duration-150 ${isSel ? "bg-[#EFEEE5]" : broken ? "bg-[#FBECEE] hover:bg-[#F7DDE0]" : "hover:bg-[#FBFAF3]"}`}
                  onClick={(e) => {
                    // Card click toggles selection; explicit buttons stop propagation.
                    if (e.target.closest("a,button,input")) return;
                    toggleSelect(d.id);
                  }}
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      data-testid={`doc-select-${d.id}`}
                      checked={isSel}
                      onChange={() => toggleSelect(d.id)}
                      onClick={(e) => e.stopPropagation()}
                      className="mt-1 w-4 h-4 accent-[#1D3557] cursor-pointer"
                    />
                    <div className={`w-10 h-10 flex items-center justify-center border shrink-0 ${broken ? "bg-white border-[#E63946]" : "bg-[#FBFAF3] border-[#DCD9D0]"}`}>
                      <Icon size={18} className={broken ? "text-[#E63946]" : "text-[#1D3557]"} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <div className="font-heading font-semibold text-[15px] truncate">{d.title}</div>
                        {broken && (
                          <span
                            data-testid={`doc-broken-${d.id}`}
                            className="shrink-0 font-mono-eo text-[9px] uppercase tracking-widest bg-[#E63946] text-white px-1.5 py-0.5 flex items-center gap-1"
                            title="The underlying file is missing from storage. Ask the uploader (or an admin) to re-upload it."
                          >
                            <AlertCircle size={10} /> Missing file
                          </span>
                        )}
                      </div>
                      <div className="font-mono-eo text-[10px] uppercase text-[#8A8A80] tracking-widest mt-0.5">{d.extension.toUpperCase()} · {(d.size / 1024).toFixed(1)} KB</div>
                      {d.description && <div className="text-[12px] text-[#5C5C54] mt-2 line-clamp-2">{d.description}</div>}
                      <div className="font-mono-eo text-[10px] text-[#8A8A80] mt-3">Uploaded by <b>{d.uploader_name}</b></div>
                      {broken && (
                        <div className="text-[11px] text-[#8A1929] mt-2 leading-snug">
                          The underlying file is no longer in storage. {canReplace ? "Click Replace to re-upload it — every file this doc is attached to will keep working." : "Ask the original uploader (or an admin) to re-upload it."}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="mt-4 flex items-center gap-2 flex-wrap">
                    {broken ? (
                      <>
                        <button disabled title="File missing from storage" data-testid={`doc-view-${d.id}`} className="eo-btn-ghost text-[12px] flex items-center gap-1.5 opacity-40 cursor-not-allowed"><Eye size={12} /> View</button>
                        <button disabled title="File missing from storage" data-testid={`doc-download-${d.id}`} className="eo-btn-ghost text-[12px] flex items-center gap-1.5 opacity-40 cursor-not-allowed"><Download size={12} /> Download</button>
                      </>
                    ) : (
                      <>
                        <a href={docPreviewUrl(d.id, { inline: true })} target="_blank" rel="noreferrer" className="eo-btn-ghost text-[12px] flex items-center gap-1.5" data-testid={`doc-view-${d.id}`}><Eye size={12} /> View</a>
                        <a href={docPreviewUrl(d.id)} className="eo-btn-ghost text-[12px] flex items-center gap-1.5" data-testid={`doc-download-${d.id}`}><Download size={12} /> Download</a>
                      </>
                    )}
                    {broken && canReplace && (
                      <button
                        data-testid={`doc-replace-${d.id}`}
                        onClick={() => promptReplace(d.id)}
                        disabled={replacing}
                        className="eo-btn-ghost text-[12px] flex items-center gap-1.5 border-[#1D3557] text-[#1D3557] hover:bg-[#1D3557]/10 disabled:opacity-40"
                      >
                        {replacing && replaceTargetId === d.id ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Replace file
                      </button>
                    )}
                    {canReplace && (
                      <button data-testid={DEPOSITORY.deleteBtn(d.id)} onClick={() => del(d.id)} className="eo-btn-ghost text-[12px] flex items-center gap-1.5 border-[#E63946] text-[#E63946] hover:bg-[#FBECEE]"><Trash2 size={12} /> Delete</button>
                    )}
                  </div>
                </div>
              );
            })}
            <Pager {...docsPaged} testid="docs-pager" className="col-span-full" />
          </div>
        </div>

        {/* Hidden file picker used by the per-row Replace button */}
        <input
          ref={replaceInputRef}
          type="file"
          accept={ACCEPT}
          onChange={onReplaceFile}
          className="hidden"
          data-testid="depository-replace-input"
        />
      </div>

      {/* Upload dialog (bulk) ---------------------------------------------- */}
      <Dialog open={openUpload} onOpenChange={(v) => { if (!v) closeUploadDialog(); else setOpenUpload(true); }}>
        <DialogContent className="border-[#DCD9D0] rounded-none max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-[22px]">Bulk upload supporting documents</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div
              onDrop={onDrop} onDragOver={onDragOver} onDragLeave={onDragLeave}
              onClick={() => inputRef.current?.click()}
              className={`border-2 border-dashed p-6 text-center cursor-pointer transition-colors duration-200 ${dragOver ? "bg-[#1D3557]/5 border-[#1D3557]" : "border-[#DCD9D0] bg-[#FBFAF3] hover:bg-[#F0EFE7]"}`}
              data-testid="dialog-drop-zone"
            >
              <Upload size={22} className="mx-auto text-[#5C5C54] mb-2" />
              <div className="text-[14px] font-medium">Drop files here or click to choose</div>
              <div className="font-mono-eo text-[11px] uppercase tracking-widest text-[#8A8A80] mt-1">PDF · DOCX · DOC — multiple files supported</div>
              <input
                ref={inputRef}
                data-testid={DEPOSITORY.uploadInput}
                type="file" multiple accept={ACCEPT}
                onChange={(e) => { enqueueFiles(e.target.files); e.target.value = ""; }}
                className="hidden"
              />
            </div>

            {queue.length > 0 && (
              <div className="border border-[#DCD9D0]">
                <div className="px-3 py-2 border-b border-[#DCD9D0] bg-[#FBFAF3] font-mono-eo text-[10px] uppercase tracking-widest text-[#8A8A80] flex items-center justify-between">
                  <span>// Queued ({queue.length})</span>
                  {!busy && <button onClick={() => setQueue([])} className="text-[#8A8A80] hover:text-[#0A0A0A]" data-testid="queue-clear">Clear all</button>}
                </div>
                <div className="max-h-[280px] overflow-auto eo-scroll">
                  {queue.map((item) => (
                    <div key={item.id} data-testid={`queue-item-${item.id}`} className="flex items-center gap-3 px-3 py-2 border-b border-[#DCD9D0] last:border-b-0">
                      <div className="w-8 h-8 flex items-center justify-center border border-[#DCD9D0] bg-[#FBFAF3] shrink-0"><FileText size={14} className="text-[#1D3557]" /></div>
                      <div className="min-w-0 flex-1">
                        <input value={item.title} onChange={(e) => updateTitle(item.id, e.target.value)} disabled={busy || item.status === "done"} className="eo-input py-1 px-2 text-[13px]" placeholder="Title" data-testid={`queue-title-${item.id}`} />
                        <div className="font-mono-eo text-[10px] uppercase tracking-widest text-[#8A8A80] mt-1 truncate">
                          {item.file.name} · {(item.file.size / 1024).toFixed(1)} KB
                          {item.error && <span className="text-[#E63946] ml-2">— {item.error}</span>}
                        </div>
                      </div>
                      <div className="shrink-0 w-6 flex items-center justify-center">
                        {item.status === "uploading" && <Loader2 size={16} className="animate-spin text-[#1D3557]" />}
                        {item.status === "done" && <CheckCircle2 size={16} className="text-[#2A9D8F]" />}
                        {item.status === "error" && <AlertCircle size={16} className="text-[#E63946]" />}
                        {(item.status === "pending" || item.status === "error") && !busy && (
                          <button onClick={() => removeQueued(item.id)} title="Remove" className="text-[#8A8A80] hover:text-[#E63946] ml-1" data-testid={`queue-remove-${item.id}`}><Trash2 size={13} /></button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <button onClick={closeUploadDialog} className="eo-btn-ghost" disabled={busy}>Cancel</button>
            <button data-testid={DEPOSITORY.submitBtn} disabled={busy || queue.length === 0} onClick={uploadAll} className="eo-btn-primary">
              {busy ? "Uploading..." : `Upload ${queue.length || ""}`.trim()}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk-attach dialog ----------------------------------------------- */}
      <Dialog open={attachOpen} onOpenChange={(v) => !attachBusy && setAttachOpen(v)}>
        <DialogContent className="border-[#DCD9D0] rounded-none max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-[22px]">Attach {selectedCount} doc{selectedCount === 1 ? "" : "s"} to a file</DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-3">
            <div className="text-[12px] text-[#5C5C54]">
              Pick a file you created or currently hold — the selected supporting docs will be added to its attachments (existing attachments are preserved).
            </div>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8A80]" />
              <input
                value={attachSearch}
                onChange={(e) => setAttachSearch(e.target.value)}
                placeholder="Search files by name..."
                className="w-full h-9 pl-9 pr-3 border border-[#DCD9D0] text-[13px] rounded-sm"
                data-testid="bulk-attach-search"
              />
            </div>
            <div className="border border-[#DCD9D0] max-h-[320px] overflow-auto eo-scroll">
              {filteredAttachFiles.length === 0 ? (
                <div className="p-4 text-[13px] text-[#8A8A80] text-center">
                  {attachFiles.length === 0
                    ? "You don't have any active files to attach to. Create a file first, or wait for one to be sent to you."
                    : "No files match your search."}
                </div>
              ) : filteredAttachFiles.map((f) => {
                const isSel = attachTargetId === f.id;
                return (
                  <button
                    key={f.id}
                    onClick={() => setAttachTargetId(f.id)}
                    data-testid={`bulk-attach-file-${f.id}`}
                    className={`w-full text-left px-4 py-2.5 border-b border-[#DCD9D0] last:border-b-0 flex items-center justify-between gap-3 transition-colors duration-150 ${isSel ? "bg-[#1D3557] text-white" : "hover:bg-[#FBFAF3]"}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-[13px] font-semibold truncate">{f.name}</div>
                      <div className={`font-mono-eo text-[10px] uppercase tracking-widest mt-0.5 ${isSel ? "text-white/70" : "text-[#8A8A80]"}`}>
                        {f.status} · held by {f.current_holder_name} · created by {f.creator_name}
                      </div>
                    </div>
                    {isSel && <CheckCircle2 size={16} className="text-white shrink-0" />}
                  </button>
                );
              })}
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setAttachOpen(false)} className="eo-btn-ghost" disabled={attachBusy}>Cancel</button>
            <button onClick={doBulkAttach} disabled={attachBusy || !attachTargetId} className="eo-btn-primary" data-testid="bulk-attach-confirm">
              {attachBusy ? "Attaching..." : `Attach ${selectedCount} doc${selectedCount === 1 ? "" : "s"}`}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
